// 🧊 ICE 후보 수집 대기 (non-trickle — 완성 SDP 1회 교환) — CallDock 에서 분리 (2026-10-07 재연결 수정, 회귀 테스트 iceGather.test.ts)
//   ⏱ 2.5s → 5s (2026-08-10) — 사내 LAN 은 host 후보가 즉시 모여 complete 되므로 영향이 없지만,
//   사외(도메인/모바일망)에서는 STUN·TURN 후보 수집이 2.5s 를 넘겨 잘리곤 했다. 후보가 빠진 채
//   협상하면 최적 경로를 못 찾아 릴레이·우회 경로로 붙고 그게 통화 끊김(버퍼링)으로 나타난다.
//   complete 되면 즉시 반환하므로 정상 환경의 연결 속도는 그대로다.
//   🕐 조용기간(quiet) 조기 종료 (2026-08-22 사용자 — "받고 3~4초 걸려 연결") — 일부 ICE 서버가
//   무응답이면 complete 가 영영 안 와 5s 타임아웃을 통째로 기다렸다(수락→연결 지연의 주범).
//   후보가 1개 이상 모인 뒤 quietMs 동안 새 후보가 없으면 수집이 사실상 끝난 것으로 보고 진행한다
//   (relay 강제 환경에선 TURN 할당 직후 후보가 한꺼번에 모이므로 700ms 면 충분히 관대하다).
//   🔴 'complete' 만 믿지 않는다 (2026-10-07 형 "안드 보이스톡 56초 지나니까 끊기고 다시 붙고") — ICE 재시작
//   (createOffer({iceRestart}) → setLocalDescription) 직후엔 iceGatheringState 가 **직전 수집의 'complete' 로 남아**
//   있다가 비동기로 'gathering' 이 된다. 그 순간 바로 반환해 reoffer·reanswer 가 **후보 0개**로 서버에 올라갔다
//   (운영 통화 ac334795 실측: offer 후보 9 · reoffer 0 · reanswer 0) → relay 강제라 새 경로를 못 찾고 재연결 실패.
//   그래서 complete 여도 로컬 SDP 에 후보(a=candidate)가 있어야 끝난 것으로 본다. 없으면 실제 수집을 기다린다(최대 timeoutMs).

/** waitIce 가 쓰는 RTCPeerConnection 의 부분 — 테스트에서 가짜로 갈아끼운다 */
export interface IceGatherTarget {
	readonly iceGatheringState: RTCIceGatheringState;
	readonly localDescription: { sdp: string } | null;
	addEventListener(type: 'icegatheringstatechange' | 'icecandidate', fn: (e: Event) => void): void;
	removeEventListener(type: 'icegatheringstatechange' | 'icecandidate', fn: (e: Event) => void): void;
}

/** 로컬 SDP 에 후보가 하나라도 실렸나 */
export const sdpHasCandidate = (sdp: string | undefined | null): boolean => !!sdp && /(^|\n)a=candidate:/.test(sdp);

export function waitIce(pc: IceGatherTarget, timeoutMs = 5000, quietMs = 700): Promise<void> {
	const ready = () => pc.iceGatheringState === 'complete' && sdpHasCandidate(pc.localDescription?.sdp);
	if (ready()) return Promise.resolve();
	return new Promise((resolve) => {
		let quietTimer: ReturnType<typeof setTimeout> | null = null;
		const done = () => {
			clearTimeout(to);
			if (quietTimer !== null) clearTimeout(quietTimer);
			pc.removeEventListener('icegatheringstatechange', chk);
			pc.removeEventListener('icecandidate', onCand);
			resolve();
		};
		const chk = () => { if (ready()) done(); };
		const onCand = (e: Event) => {
			if (!(e as RTCPeerConnectionIceEvent).candidate) return;   // null = 수집 종료 신호 — complete 이벤트가 처리
			if (quietTimer !== null) clearTimeout(quietTimer);
			quietTimer = setTimeout(done, quietMs);
		};
		const to = setTimeout(done, timeoutMs);
		pc.addEventListener('icegatheringstatechange', chk);
		pc.addEventListener('icecandidate', onCand);
	});
}
